import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import {
  Box,
  Paper,
  Typography,
  ToggleButton,
  ToggleButtonGroup,
  TextField,
  MenuItem,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  TablePagination,
  CircularProgress,
  Alert,
  InputAdornment,
  Chip,
  Stack,
  Button,
  Tooltip,
  Checkbox,
  ListItemText,
  Menu,
  IconButton,
  Autocomplete,
} from '@mui/material';
import { Search, Insights, Refresh, ViewColumn, MenuBook, CompareArrows, Close } from '@mui/icons-material';
import ProjectionsGlossary from '../components/ProjectionsGlossary';
import apiService, { ProjectionRow, ProjectionsResponse } from '../services/api';
import { getRoleLabel } from '../utils/roles';

type Mode = 'batters' | 'bowlers';

// Projections change rarely, so we cache them: in-memory for fast in-session
// reuse, and mirrored into sessionStorage so a full page refresh restores the
// table instantly without re-loading. The Refresh button clears both.
const projectionsCache = new Map<string, ProjectionsResponse>();
const cacheKey = (mode: Mode, season: number | null) => `${mode}:${season ?? 'latest'}`;
// Bump the version whenever the row shape changes (v3 added overall_role; v4
// added matches_played, batting_role, bowling_role) so cached tabs can't render
// a stale payload missing the new columns.
const SS_PREFIX = 'proj_cache_v4:';

const readCache = (key: string): ProjectionsResponse | null => {
  const mem = projectionsCache.get(key);
  if (mem) return mem;
  try {
    const raw = sessionStorage.getItem(SS_PREFIX + key);
    if (raw) {
      const parsed = JSON.parse(raw) as ProjectionsResponse;
      projectionsCache.set(key, parsed);
      return parsed;
    }
  } catch {
    /* sessionStorage unavailable — fall through to network */
  }
  return null;
};

const writeCache = (key: string, resp: ProjectionsResponse) => {
  projectionsCache.set(key, resp);
  try {
    sessionStorage.setItem(SS_PREFIX + key, JSON.stringify(resp));
  } catch {
    /* storage full/unavailable — in-memory cache still works */
  }
};

const clearCache = () => {
  projectionsCache.clear();
  try {
    Object.keys(sessionStorage)
      .filter((k) => k.startsWith(SS_PREFIX))
      .forEach((k) => sessionStorage.removeItem(k));
  } catch {
    /* ignore */
  }
};

// Column used for the default (total value) sort, per mode
const totalValueKey = (mode: Mode) =>
  mode === 'batters' ? 'batter_total_value_hawkeye_proj' : 'bowler_total_value_hawkeye_proj';

// Sample-size column used for the minimum-workload filter, per mode
const sampleKey = (mode: Mode) => (mode === 'batters' ? 'balls_faced' : 'deliveries');

// Width of the frozen serial-number column; the first frozen column starts here.
const SNO_WIDTH = 56;


// Role vocabularies from v_player_roles that get their own filter.
const ROLE_FILTER_KEYS = ['overall_role', 'batting_role', 'bowling_role'] as const;

// Columns hidden by default; the "Show" control unhides them per session. The
// Overall Role FILTER keeps working while its column is hidden.
const OPTIONAL_COLUMNS: { key: string; label: string }[] = [
  { key: 'overall_role', label: 'Overall role' },
  { key: 'matches_played', label: 'Matches' },
  { key: 'target_season', label: 'Season' },
];
type BatterMetric = 'batter' | 'shot';
type BowlerMetric = 'ball' | 'bowler';
type ValueBasis = 'per_innings' | 'per_ball';
type Order = 'asc' | 'desc';

interface Column {
  key: string;
  label: string;
  numeric: boolean;
  decimals?: number; // number of decimals for numeric formatting
}

// Display labels for phase codes — keep the short cricket abbreviations.
const PHASE_LABELS: Record<string, string> = {
  pp: 'PP',
  'post-pp': 'Post-PP',
  mo: 'MO',
  so: 'SO',
  all: 'All',
};

const phaseLabel = (v: string): string =>
  PHASE_LABELS[v] ?? (v ? v.toUpperCase() : v);

const paceSpinLabel = (v: string): string =>
  v ? v.charAt(0).toUpperCase() + v.slice(1) : v;

// Build the column set for the current mode / metric selections.
const getColumns = (
  mode: Mode,
  batterMetric: BatterMetric,
  bowlerMetric: BowlerMetric
): Column[] => {
  if (mode === 'batters') {
    return [
      { key: 'player_name', label: 'Player', numeric: false },
      { key: 'batting_role', label: 'Batting Role', numeric: false },
      { key: 'nationality', label: 'Nationality', numeric: false },
      { key: 'overall_role', label: 'Overall Role', numeric: false },
      { key: 'target_season', label: 'Season', numeric: true },
      { key: 'pace_or_spin', label: 'Pace/Spin', numeric: false },
      { key: 'phase', label: 'Phase', numeric: false },
      { key: 'matches_played', label: 'Matches', numeric: true, decimals: 0 },
      { key: 'innings', label: 'Innings', numeric: true },
      { key: 'balls_faced', label: 'Balls', numeric: true },
      { key: 'balls_per_innings', label: 'Balls/Inns', numeric: true, decimals: 0 },
      { key: 'batter_dismissal_value_hawkeye_proj', label: 'Dismissal Value', numeric: true, decimals: 4 },
      // Toggle column: Batter Run Value <-> Shot Run Value
      batterMetric === 'batter'
        ? { key: 'batter_run_value_hawkeye_proj', label: 'Batter Run Value', numeric: true, decimals: 3 }
        : { key: 'shot_run_value_hawkeye_proj', label: 'Shot Run Value', numeric: true, decimals: 3 },
      { key: 'batter_total_value_hawkeye_proj', label: 'Total Value', numeric: true, decimals: 3 },
    ];
  }
  // bowlers
  const isBall = bowlerMetric === 'ball';
  return [
    { key: 'player_name', label: 'Player', numeric: false },
    { key: 'bowling_role', label: 'Bowling Role', numeric: false },
    { key: 'nationality', label: 'Nationality', numeric: false },
    { key: 'overall_role', label: 'Overall Role', numeric: false },
    { key: 'target_season', label: 'Season', numeric: true },
    { key: 'pace_or_spin', label: 'Pace/Spin', numeric: false },
    { key: 'phase', label: 'Phase', numeric: false },
    { key: 'matches_played', label: 'Matches', numeric: true, decimals: 0 },
    { key: 'innings', label: 'Innings', numeric: true },
    { key: 'deliveries', label: 'Balls', numeric: true },
    { key: 'balls_per_innings', label: 'Balls/Inns', numeric: true, decimals: 0 },
    // Toggle columns: Ball <-> Bowler (run value + dismissal value)
    isBall
      ? { key: 'ball_run_value_hawkeye_proj', label: 'Ball Run Value', numeric: true, decimals: 3 }
      : { key: 'bowler_run_value_hawkeye_proj', label: 'Bowler Run Value', numeric: true, decimals: 3 },
    isBall
      ? { key: 'ball_dismissal_value_hawkeye_proj', label: 'Ball Dismissal Value', numeric: true, decimals: 3 }
      : { key: 'bowler_dismissal_value_hawkeye_proj', label: 'Bowler Dismissal Value', numeric: true, decimals: 3 },
    { key: 'bowler_total_value_hawkeye_proj', label: 'Total Value', numeric: true, decimals: 3 },
    // Shot run/dismissal values intentionally hidden for bowlers for now
    // (to be implemented in future).
  ];
};

const formatValue = (value: string | number | null, col: Column): string => {
  if (value === null || value === undefined || value === '') return '—';
  if (col.numeric) {
    const num = typeof value === 'number' ? value : Number(value);
    if (Number.isNaN(num)) return String(value);
    if (col.decimals !== undefined) return num.toFixed(col.decimals);
    return String(num);
  }
  if (col.key === 'phase') return phaseLabel(String(value));
  if (col.key === 'pace_or_spin') return paceSpinLabel(String(value));
  if (col.key === 'overall_role' || col.key === 'batting_role' || col.key === 'bowling_role')
    return getRoleLabel(String(value));
  return String(value);
};

const Projections: React.FC = () => {
  const [glossaryOpen, setGlossaryOpen] = useState(false);
  const [mode, setMode] = useState<Mode>('batters');
  const [batterMetric, setBatterMetric] = useState<BatterMetric>('batter');
  const [bowlerMetric, setBowlerMetric] = useState<BowlerMetric>('bowler');

  const [rows, setRows] = useState<ProjectionRow[]>([]);
  const [availableSeasons, setAvailableSeasons] = useState<number[]>([]);
  const [season, setSeason] = useState<number | null>(null);
  const [loading, setLoading] = useState<boolean>(false);
  const [refreshing, setRefreshing] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);

  // Filters — default to the aggregate rows: Phase = "all", Pace/Spin = "combined"
  const [search, setSearch] = useState<string>('');
  // Players picked for side-by-side comparison (names; cleared when switching batters/bowlers)
  // Kept per tab, so switching between batters and bowlers restores each list
  const [compareByMode, setCompareByMode] = useState<Record<Mode, string[]>>({ batters: [], bowlers: [] } as Record<Mode, string[]>);
  const compare = useMemo(() => compareByMode[mode] || [], [compareByMode, mode]);
  const setCompare = (next: string[] | ((prev: string[]) => string[])) =>
    setCompareByMode((all) => ({ ...all, [mode]: typeof next === 'function' ? next(all[mode] || []) : next }));
  // Multi-select filters: empty selection = no restriction. Defaults pick the
  // aggregate rows ('all' phase, 'combined' pace/spin) like before.
  const [phaseFilter, setPhaseFilter] = useState<string[]>(['all']);
  const [paceSpinFilter, setPaceSpinFilter] = useState<string>('combined');
  // Minimum workload: balls faced (batters) / deliveries (bowlers). Defaults to 100.
  const [minSample, setMinSample] = useState<string>('100');
  const [nationalityFilter, setNationalityFilter] = useState<string[]>([]);
  const [overallRoleFilter, setOverallRoleFilter] = useState<string[]>([]);
  const [subRoleFilter, setSubRoleFilter] = useState<string[]>([]);
  const [colMenuAnchor, setColMenuAnchor] = useState<null | HTMLElement>(null);
  // Optional-column keys currently shown (all hidden by default).
  const [shownOptionalCols, setShownOptionalCols] = useState<string[]>([]);
  const [valueBasis, setValueBasis] = useState<ValueBasis>('per_innings');

  // Sorting & pagination — default: sort by total value, highest first
  const [orderBy, setOrderBy] = useState<string>(totalValueKey('batters'));
  const [order, setOrder] = useState<Order>('desc');
  const [page, setPage] = useState<number>(0);
  const [rowsPerPage, setRowsPerPage] = useState<number>(25);

  const applyResponse = useCallback((resp: ProjectionsResponse) => {
    // Normalize phase / pace_or_spin casing so the default filters ('all',
    // 'combined') and the derived dropdown options always match the data,
    // regardless of how the warehouse stores the casing.
    const norm = (v: string | number | null) =>
      v === null || v === undefined ? v : String(v).toLowerCase();
    const normalizedRows = (resp.rows || []).map((r) => ({
      ...r,
      phase: norm(r['phase']),
      pace_or_spin: norm(r['pace_or_spin']),
    }));
    setRows(normalizedRows);
    setAvailableSeasons(resp.available_seasons || []);
    setSeason(resp.target_season);
  }, []);

  const fetchData = useCallback(
    async (m: Mode, s: number | null, force = false) => {
      // Serve from cache (memory or sessionStorage) unless forcing a reload.
      if (!force) {
        const cached = readCache(cacheKey(m, s));
        if (cached) {
          setError(null);
          setLoading(false);
          applyResponse(cached);
          return;
        }
      }

      setLoading(true);
      setError(null);
      try {
        const resp =
          m === 'batters'
            ? await apiService.getBatterProjections(s ?? undefined)
            : await apiService.getBowlerProjections(s ?? undefined);
        // Cache under the requested key and the resolved-season key so both
        // the "latest" default and the explicit season hit the cache next time.
        writeCache(cacheKey(m, s), resp);
        if (resp.target_season != null) {
          writeCache(cacheKey(m, resp.target_season), resp);
        }
        applyResponse(resp);
      } catch (e: any) {
        setError(e?.detail || e?.message || 'Failed to load projections');
        setRows([]);
      } finally {
        setLoading(false);
      }
    },
    [applyResponse]
  );

  const handleRefresh = useCallback(async () => {
    setRefreshing(true);
    setError(null);
    try {
      // Rebuild the backend cache from BigQuery, then reload the current view.
      await apiService.refreshProjections();
    } catch {
      /* even if the backend refresh errors, still drop local caches + refetch */
    }
    clearCache();
    try {
      await fetchData(mode, season, true);
    } finally {
      setRefreshing(false);
    }
  }, [fetchData, mode, season]);

  // Initial load
  useEffect(() => {
    fetchData('batters', null);
  }, [fetchData]);

  const handleModeChange = (_e: React.MouseEvent<HTMLElement>, newMode: Mode | null) => {
    if (!newMode || newMode === mode) return;
    setMode(newMode);
    setPage(0);
    setOrderBy(totalValueKey(newMode));
    setOrder('desc');
    fetchData(newMode, season);
  };

  const handleSeasonChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newSeason = Number(e.target.value);
    setSeason(newSeason);
    setPage(0);
    fetchData(mode, newSeason);
  };

  const columns = useMemo(
    () =>
      getColumns(mode, batterMetric, bowlerMetric).filter(
        (c) =>
          !OPTIONAL_COLUMNS.some((o) => o.key === c.key) ||
          shownOptionalCols.includes(c.key)
      ),
    [mode, batterMetric, bowlerMetric, shownOptionalCols]
  );

  // Distinct values for filter dropdowns (derived from loaded rows).
  const phaseOptions = useMemo(() => {
    const set = new Set<string>();
    rows.forEach((r) => {
      const v = r['phase'];
      if (v !== null && v !== undefined && v !== '') set.add(String(v));
    });
    return Array.from(set).sort();
  }, [rows]);

  const paceSpinOptions = useMemo(() => {
    const set = new Set<string>();
    rows.forEach((r) => {
      const v = r['pace_or_spin'];
      if (v !== null && v !== undefined && v !== '') set.add(String(v));
    });
    return Array.from(set).sort();
  }, [rows]);

  // The selections actually shown/applied: intersect with the loaded data's
  // options, so a selection that doesn't exist in this mode (e.g. bowlers have
  // no "combined" pace/spin) is ignored rather than emptying the table. When a
  // non-empty selection intersects to nothing, fall back to the aggregate rows.
  const effectivePhase = useMemo(() => {
    const eff = phaseFilter.filter((v) => phaseOptions.includes(v));
    if (eff.length || !phaseFilter.length) return eff;
    return phaseOptions.includes('all') ? ['all'] : [];
  }, [phaseOptions, phaseFilter]);

  // Single-select: the pace/spin splits are alternative views of the same
  // deliveries, so exactly one is active; fall back to a valid option when the
  // stored choice doesn't exist in this mode.
  const effectivePaceSpin = useMemo(() => {
    if (!paceSpinOptions.length) return paceSpinFilter;
    if (paceSpinOptions.includes(paceSpinFilter)) return paceSpinFilter;
    return paceSpinOptions.includes('combined') ? 'combined' : paceSpinOptions[0];
  }, [paceSpinOptions, paceSpinFilter]);

  // NOTE: we deliberately do NOT sync the resolved values back into state.
  // The raw filters hold the user's intent (defaults 'all' / 'combined'); the
  // effective values above adapt per mode for display + filtering only, so the
  // batter default ('combined') survives a visit to the bowlers tab.

  const handleSort = (key: string) => {
    if (orderBy === key) {
      setOrder(order === 'asc' ? 'desc' : 'asc');
    } else {
      setOrderBy(key);
      setOrder('asc');
    }
    setPage(0);
  };

  const nationalityOptions = useMemo(() => {
    const set = new Set<string>();
    rows.forEach((r) => {
      const n = String(r['nationality'] ?? '').trim();
      if (n) set.add(n);
    });
    return Array.from(set).sort();
  }, [rows]);
  const effectiveNationality = useMemo(
    () => nationalityFilter.filter((v) => nationalityOptions.includes(v)),
    [nationalityFilter, nationalityOptions]
  );

  // Distinct values for each role vocabulary, labelled and sorted for the filters.
  const roleOptionSets = useMemo(() => {
    const sets: Record<string, string[]> = {};
    ROLE_FILTER_KEYS.forEach((key) => {
      const set = new Set<string>();
      rows.forEach((r) => {
        const v = String(r[key] ?? '').trim();
        if (v) set.add(v);
      });
      sets[key] = Array.from(set).sort((a, b) =>
        getRoleLabel(a).localeCompare(getRoleLabel(b))
      );
    });
    return sets;
  }, [rows]);

  // 'bowler' as an overall role is meaningless on the batters tab — hide it.
  const overallRoleOptions = useMemo(() => {
    const opts = roleOptionSets['overall_role'] ?? [];
    return mode === 'batters' ? opts.filter((v) => v !== 'bowler') : opts;
  }, [roleOptionSets, mode]);
  const effectiveOverallRole = useMemo(
    () => overallRoleFilter.filter((v) => overallRoleOptions.includes(v)),
    [overallRoleFilter, overallRoleOptions]
  );

  // Batting role for batters, bowling role for bowlers — one control; selections
  // from the other mode's vocabulary are ignored via the intersection above.
  const subRoleKey = mode === 'batters' ? 'batting_role' : 'bowling_role';
  const subRoleOptions = roleOptionSets[subRoleKey] ?? [];
  const effectiveSubRole = useMemo(
    () => subRoleFilter.filter((v) => subRoleOptions.includes(v)),
    [subRoleFilter, subRoleOptions]
  );

  // Metric scale is a display choice. 'per_innings' multiplies every Hawkeye
  // projection by that row's balls_per_innings; 'per_ball' shows the stored
  // values untouched. Applied before filtering and sorting so ordering matches
  // what the user sees; the API rows (and the session cache) always stay raw.
  const scaledRows = useMemo(
    () =>
      valueBasis === 'per_ball'
        ? rows
        : rows.map((r) => {
            const bpi = Number(r['balls_per_innings']);
            const out: typeof r = { ...r };
            for (const key of Object.keys(r)) {
              if (!key.endsWith('_hawkeye_proj')) continue;
              const v = Number(r[key]);
              out[key] =
                Number.isFinite(bpi) && bpi > 0 && Number.isFinite(v) ? v * bpi : null;
            }
            return out;
          }),
    [rows, valueBasis]
  );



  const filteredSortedRows = useMemo(() => {
    const term = search.trim().toLowerCase();
    const minVal = minSample.trim() === '' ? 0 : Number(minSample);
    const workloadKey = sampleKey(mode);
    let result = scaledRows.filter((r) => {
      // Compared players ignore the search, nationality, role and minimum-sample filters;
      // only the phase and pace/spin view applies to them.
      // While comparing, the table shows only the compared players
      if (compare.length) {
        if (!compare.includes(String(r['player_name'] ?? ''))) return false;
        if (effectivePhase.length && !effectivePhase.includes(String(r['phase'] ?? ''))) return false;
        if (effectivePaceSpin && String(r['pace_or_spin'] ?? '') !== effectivePaceSpin) return false;
        return true;
      }
      if (term) {
        const name = String(r['player_name'] ?? '').toLowerCase();
        if (!name.includes(term)) return false;
      }
      if (effectivePhase.length && !effectivePhase.includes(String(r['phase'] ?? ''))) return false;
      if (effectivePaceSpin && String(r['pace_or_spin'] ?? '') !== effectivePaceSpin) return false;
      if (effectiveNationality.length && !effectiveNationality.includes(String(r['nationality'] ?? '')))
        return false;
      if (effectiveOverallRole.length && !effectiveOverallRole.includes(String(r['overall_role'] ?? '')))
        return false;
      if (effectiveSubRole.length && !effectiveSubRole.includes(String(r[subRoleKey] ?? '')))
        return false;
      if (minVal > 0) {
        const w = Number(r[workloadKey]);
        if (Number.isNaN(w) || w < minVal) return false;
      }
      return true;
    });

    const col = columns.find((c) => c.key === orderBy);
    const numeric = col?.numeric ?? false;

    result = [...result].sort((a, b) => {
      const av = a[orderBy];
      const bv = b[orderBy];
      // Nulls always sort to the bottom regardless of direction.
      const aNull = av === null || av === undefined || av === '';
      const bNull = bv === null || bv === undefined || bv === '';
      if (aNull && bNull) return 0;
      if (aNull) return 1;
      if (bNull) return -1;

      let cmp: number;
      if (numeric) {
        cmp = Number(av) - Number(bv);
      } else {
        cmp = String(av).localeCompare(String(bv));
      }
      return order === 'asc' ? cmp : -cmp;
    });

    // Compared players stay pinned at the top, in the order they were picked
    if (compare.length) {
      const pinned = compare.flatMap((n) => result.filter((r) => String(r['player_name'] ?? '') === n));
      result = [...pinned, ...result.filter((r) => !compare.includes(String(r['player_name'] ?? '')))];
    }
    return result;
  }, [scaledRows, search, compare, effectivePhase, effectivePaceSpin, effectiveNationality, effectiveOverallRole, effectiveSubRole, subRoleKey, minSample, mode, columns, orderBy, order]);

  const pagedRows = useMemo(
    () => filteredSortedRows.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage),
    [filteredSortedRows, page, rowsPerPage]
  );

  const playerOptions = useMemo(() => {
    const set = new Set<string>();
    rows.forEach((r) => { const n = r['player_name']; if (n !== null && n !== undefined && n !== '') set.add(String(n)); });
    return Array.from(set).sort((a, b) => a.localeCompare(b));
  }, [rows]);
  const toggleCompare = (name: string) => {
    setCompare((prev) => (prev.includes(name) ? prev.filter((n) => n !== name) : [...prev, name]));
    setPage(0);
  };

  // Heat map for the compared players: per value column (and per phase and pace/spin view),
  // the highest value is deep green, the lowest soft red, and the rest graded in between.
  const compareRange = useMemo(() => {
    const range: Record<string, { min: number; max: number }> = {};
    if (compare.length < 2) return range;
    const valueCols = columns.filter((c) => c.numeric && c.key.endsWith('_hawkeye_proj'));
    filteredSortedRows.forEach((r) => {
      if (!compare.includes(String(r['player_name'] ?? ''))) return;
      const view = `${r['phase'] ?? ''}|${r['pace_or_spin'] ?? ''}`;
      valueCols.forEach((c) => {
        const v = Number(r[c.key]); if (!Number.isFinite(v)) return;
        const k = `${view}|${c.key}`; const cur = range[k];
        range[k] = cur ? { min: Math.min(cur.min, v), max: Math.max(cur.max, v) } : { min: v, max: v };
      });
    });
    return range;
  }, [compare, columns, filteredSortedRows]);
  const heatOf = (r: ProjectionRow, key: string): { bg: string; strong: boolean } | null => {
    if (!compare.includes(String(r['player_name'] ?? ''))) return null;
    const rg = compareRange[`${r['phase'] ?? ''}|${r['pace_or_spin'] ?? ''}|${key}`];
    const v = Number(r[key]);
    if (!rg || !Number.isFinite(v) || rg.max === rg.min) return null;
    const t = (v - rg.min) / (rg.max - rg.min); // 0 = lowest, 1 = highest
    const strong = t >= 0.999 || t <= 0.001;
    if (t >= 0.5) return { bg: `rgba(46, 125, 50, ${(0.06 + (t - 0.5) * 2 * 0.34).toFixed(3)})`, strong };
    return { bg: `rgba(211, 47, 47, ${(0.04 + (0.5 - t) * 2 * 0.22).toFixed(3)})`, strong };
  };

  // Distinct player count across the filtered set
  const playerCount = useMemo(() => {
    const set = new Set<string>();
    filteredSortedRows.forEach((r) => {
      const n = r['player_name'];
      if (n !== null && n !== undefined && n !== '') set.add(String(n));
    });
    return set.size;
  }, [filteredSortedRows]);


  return (
    <Box sx={{ p: 3, maxWidth: 1600, mx: 'auto' }}>
      {/* Header */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 0.5 }}>
        <Insights color="primary" sx={{ fontSize: 32 }} />
        <Typography variant="h4" fontWeight={700}>
          Projections
        </Typography>
        <Box sx={{ flex: 1 }} />
        <Button size="small" variant="outlined" color="secondary" startIcon={<MenuBook />} onClick={() => setGlossaryOpen(true)} sx={{ textTransform: 'none' }}>
          Glossary
        </Button>
      </Box>
      <ProjectionsGlossary open={glossaryOpen} onClose={() => setGlossaryOpen(false)} />
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        Hawk-eye projected value splits by phase and pace/spin for batters and bowlers.{' '}
        {valueBasis === 'per_innings' ? (
          <>
            Values are shown <strong>per innings</strong> (per-ball projection ×
            balls/innings) — switch to <strong>Per ball</strong> below for raw values.
          </>
        ) : (
          <>
            Values are shown <strong>per ball</strong>, exactly as stored — switch to{' '}
            <strong>Per innings</strong> below to scale by balls/innings.
          </>
        )}
      </Typography>

      {/* Controls */}
      <Paper elevation={1} sx={{ p: 2, mb: 2, borderRadius: 2 }}>
        <Stack
          direction={{ xs: 'column', md: 'row' }}
          spacing={2}
          alignItems={{ xs: 'stretch', md: 'center' }}
          flexWrap="wrap"
          useFlexGap
        >
          {/* Batters / Bowlers */}
          <ToggleButtonGroup
            value={mode}
            exclusive
            onChange={handleModeChange}
            color="primary"
            size="small"
          >
            <ToggleButton value="batters" sx={{ px: 2, fontWeight: 600 }}>
              Batters
            </ToggleButton>
            <ToggleButton value="bowlers" sx={{ px: 2, fontWeight: 600 }}>
              Bowlers
            </ToggleButton>
          </ToggleButtonGroup>

          {/* Season */}
          <TextField
            select
            label="Season"
            size="small"
            value={availableSeasons.length && season != null ? season : ''}
            onChange={handleSeasonChange}
            sx={{ minWidth: 130 }}
            disabled={loading || availableSeasons.length === 0}
          >
            {availableSeasons.map((s) => (
              <MenuItem key={s} value={s}>
                {s}
              </MenuItem>
            ))}
          </TextField>

          {/* Player search */}
          <TextField
            label="Search player"
            size="small"
            value={search}
            onChange={(e) => {
              setSearch(e.target.value);
              setPage(0);
            }}
            sx={{ minWidth: 200, flexGrow: 1 }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <Search fontSize="small" />
                </InputAdornment>
              ),
            }}
          />

          {/* Phase filter */}
          <TextField
            select
            label="Phase"
            size="small"
            value={effectivePhase}
            onChange={(e) => {
              setPhaseFilter(
                typeof e.target.value === 'string' ? e.target.value.split(',') : e.target.value
              );
              setPage(0);
            }}
            SelectProps={{
              multiple: true,
              displayEmpty: true,
              renderValue: (sel) => {
                const v = sel as string[];
                return v.length ? v.map(phaseLabel).join(', ') : 'All';
              },
            }}
            InputLabelProps={{ shrink: true }}
            sx={{ minWidth: 160 }}
          >
            {phaseOptions.map((p) => (
              <MenuItem key={p} value={p} dense>
                <Checkbox size="small" checked={effectivePhase.includes(p)} sx={{ py: 0 }} />
                <ListItemText primary={phaseLabel(p)} />
              </MenuItem>
            ))}
          </TextField>

          {/* Pace / Spin filter — single select: the splits are alternative
              views of the same deliveries, so exactly one is active */}
          <TextField
            select
            label="Pace/Spin"
            size="small"
            value={effectivePaceSpin}
            onChange={(e) => {
              setPaceSpinFilter(e.target.value);
              setPage(0);
            }}
            sx={{ minWidth: 140 }}
          >
            {paceSpinOptions.map((p) => (
              <MenuItem key={p} value={p}>
                {paceSpinLabel(p)}
              </MenuItem>
            ))}
          </TextField>

          {/* Nationality filter */}
          <TextField
            select
            label="Nationality"
            size="small"
            value={effectiveNationality}
            onChange={(e) => {
              setNationalityFilter(
                typeof e.target.value === 'string' ? e.target.value.split(',') : e.target.value
              );
              setPage(0);
            }}
            SelectProps={{
              multiple: true,
              displayEmpty: true,
              renderValue: (sel) => {
                const v = sel as string[];
                return v.length ? v.join(', ') : 'All';
              },
            }}
            InputLabelProps={{ shrink: true }}
            sx={{ minWidth: 160 }}
          >
            {nationalityOptions.map((n) => (
              <MenuItem key={n} value={n} dense>
                <Checkbox size="small" checked={effectiveNationality.includes(n)} sx={{ py: 0 }} />
                <ListItemText primary={n} />
              </MenuItem>
            ))}
          </TextField>

          {/* Role filters from the player-roles view: overall, then the
              mode-specific batting/bowling role */}
          <TextField
            select
            label="Overall Role"
            size="small"
            value={effectiveOverallRole}
            onChange={(e) => {
              setOverallRoleFilter(
                typeof e.target.value === 'string' ? e.target.value.split(',') : e.target.value
              );
              setPage(0);
            }}
            SelectProps={{
              multiple: true,
              displayEmpty: true,
              renderValue: (sel) => {
                const v = sel as string[];
                return v.length ? v.map(getRoleLabel).join(', ') : 'All';
              },
            }}
            InputLabelProps={{ shrink: true }}
            sx={{ minWidth: 160 }}
          >
            {overallRoleOptions.map((r) => (
              <MenuItem key={r} value={r} dense>
                <Checkbox size="small" checked={effectiveOverallRole.includes(r)} sx={{ py: 0 }} />
                <ListItemText primary={getRoleLabel(r)} />
              </MenuItem>
            ))}
          </TextField>

          <TextField
            select
            label={mode === 'batters' ? 'Batting Role' : 'Bowling Role'}
            size="small"
            value={effectiveSubRole}
            onChange={(e) => {
              setSubRoleFilter(
                typeof e.target.value === 'string' ? e.target.value.split(',') : e.target.value
              );
              setPage(0);
            }}
            SelectProps={{
              multiple: true,
              displayEmpty: true,
              renderValue: (sel) => {
                const v = sel as string[];
                return v.length ? v.map(getRoleLabel).join(', ') : 'All';
              },
            }}
            InputLabelProps={{ shrink: true }}
            sx={{ minWidth: 160 }}
          >
            {subRoleOptions.map((r) => (
              <MenuItem key={r} value={r} dense>
                <Checkbox size="small" checked={effectiveSubRole.includes(r)} sx={{ py: 0 }} />
                <ListItemText primary={getRoleLabel(r)} />
              </MenuItem>
            ))}
          </TextField>

          {/* Minimum workload filter: balls faced (batters) / deliveries (bowlers) */}
          <TextField
            type="number"
            label={mode === 'batters' ? 'Min balls faced' : 'Min deliveries'}
            size="small"
            value={minSample}
            onChange={(e) => {
              setMinSample(e.target.value);
              setPage(0);
            }}
            inputProps={{ min: 0 }}
            sx={{ minWidth: 150 }}
          />
        </Stack>

        {/* Metric toggle row */}
        <Box sx={{ mt: 2, display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap' }}>
          {mode === 'batters' ? (
            <>
              <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 600 }}>
                Run value:
              </Typography>
              <ToggleButtonGroup
                value={batterMetric}
                exclusive
                size="small"
                color="secondary"
                onChange={(_e, v: BatterMetric | null) => v && setBatterMetric(v)}
              >
                <ToggleButton value="batter" sx={{ px: 2 }}>
                  Batter
                </ToggleButton>
                <ToggleButton value="shot" sx={{ px: 2 }}>
                  Shot
                </ToggleButton>
              </ToggleButtonGroup>
              <Typography variant="caption" color={batterMetric === 'shot' ? 'warning.main' : 'text.secondary'} sx={{ fontWeight: batterMetric === 'shot' ? 600 : 400 }}>
                {batterMetric === 'shot'
                  ? 'Shot run value is one part of the batter run value. Total Value stays batter run value + wicket run value × dismissal value.'
                  : 'Total Value = batter run value + wicket run value × dismissal value.'}
              </Typography>
            </>
          ) : (
            <>
              <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 600 }}>
                Run &amp; dismissal values:
              </Typography>
              <ToggleButtonGroup
                value={bowlerMetric}
                exclusive
                size="small"
                color="secondary"
                onChange={(_e, v: BowlerMetric | null) => v && setBowlerMetric(v)}
              >
                <ToggleButton value="ball" sx={{ px: 2 }}>
                  Ball
                </ToggleButton>
                <ToggleButton value="bowler" sx={{ px: 2 }}>
                  Bowler
                </ToggleButton>
              </ToggleButtonGroup>
            </>
          )}


          {/* Scale toggle: per-innings (× balls/innings) vs raw per-ball values */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, ml: { sm: 2 } }}>
            <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 600 }}>
              Values:
            </Typography>
            <ToggleButtonGroup
              value={valueBasis}
              exclusive
              size="small"
              color="primary"
              onChange={(_e, v: ValueBasis | null) => v && setValueBasis(v)}
            >
              <Tooltip title="Per-ball projection × balls per innings">
                <ToggleButton value="per_innings" sx={{ px: 2 }}>
                  Per innings
                </ToggleButton>
              </Tooltip>
              <Tooltip title="Raw per-ball projections, exactly as stored">
                <ToggleButton value="per_ball" sx={{ px: 2 }}>
                  Per ball
                </ToggleButton>
              </Tooltip>
            </ToggleButtonGroup>
          </Box>

        </Box>
      </Paper>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {/* Compare players */}
      <Paper elevation={1} sx={{ p: 2, mb: 2, borderRadius: 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap' }}>
          <CompareArrows color="primary" />
          <Typography sx={{ fontWeight: 700 }}>Compare players</Typography>
          <Autocomplete
            multiple
            size="small"
            options={playerOptions}
            value={compare}
            onChange={(_e, v) => { setCompare(v); setPage(0); }}
            filterSelectedOptions
            autoHighlight
            sx={{ flex: 1, minWidth: 320 }}
            renderInput={(params) => (
              <TextField {...params} placeholder={compare.length ? 'Add another player' : `Pick ${mode === 'batters' ? 'batters' : 'bowlers'} to compare, or tick them in the table`} />
            )}
          />
          {compare.length > 0 && (
            <Button size="small" startIcon={<Close fontSize="small" />} onClick={() => { setCompare([]); setPage(0); }} sx={{ textTransform: 'none' }}>
              Clear
            </Button>
          )}
        </Box>
        {compare.length > 0 && (
          <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 1 }}>
            The table shows only the compared players, whatever the search, role or minimum-balls filters{compare.length > 1 ? '. Value columns are a heat map across them: green is higher, red is lower, the strongest shade marks the top and bottom' : ''}. Add more in the box above, or Clear to see everyone again.
          </Typography>
        )}
      </Paper>

      {/* Table */}
      <Paper elevation={1} sx={{ borderRadius: 2, overflow: 'hidden' }}>
        <Box
          sx={{
            px: 2,
            py: 1,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            borderBottom: '1px solid',
            borderColor: 'divider',
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Chip
              label={`${playerCount} player${playerCount === 1 ? '' : 's'}`}
              size="small"
              color="primary"
            />
            <Chip
              label={`${filteredSortedRows.length} row${filteredSortedRows.length === 1 ? '' : 's'}`}
              size="small"
              color="primary"
              variant="outlined"
            />
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
            {season != null && (
              <Typography variant="body2" color="text.secondary">
                Season {season}
              </Typography>
            )}
            <Tooltip title="Show or hide optional columns">
              <IconButton size="small" onClick={(e) => setColMenuAnchor(e.currentTarget)}>
                <ViewColumn fontSize="small" />
              </IconButton>
            </Tooltip>
            <Menu
              anchorEl={colMenuAnchor}
              open={Boolean(colMenuAnchor)}
              onClose={() => setColMenuAnchor(null)}
            >
              {OPTIONAL_COLUMNS.map((o) => (
                <MenuItem
                  key={o.key}
                  dense
                  onClick={() =>
                    setShownOptionalCols((prev) =>
                      prev.includes(o.key)
                        ? prev.filter((k) => k !== o.key)
                        : [...prev, o.key]
                    )
                  }
                >
                  <Checkbox
                    size="small"
                    checked={shownOptionalCols.includes(o.key)}
                    sx={{ py: 0 }}
                  />
                  <ListItemText primary={o.label} />
                </MenuItem>
              ))}
            </Menu>
            <Tooltip title="Reload the latest projections from the database">
              <span>
                <Button
                  size="small"
                  variant="outlined"
                  color="primary"
                  onClick={handleRefresh}
                  disabled={refreshing || loading}
                  startIcon={
                    refreshing ? (
                      <CircularProgress size={14} color="inherit" />
                    ) : (
                      <Refresh fontSize="small" />
                    )
                  }
                >
                  {refreshing ? 'Refreshing…' : 'Refresh'}
                </Button>
              </span>
            </Tooltip>
          </Box>
        </Box>

        <TableContainer sx={{ maxHeight: 'calc(100vh - 360px)' }}>
          <Table stickyHeader size="small">
            <TableHead>
              <TableRow>
                <TableCell
                  align="center"
                  sx={{
                    fontWeight: 700,
                    whiteSpace: 'nowrap',
                    bgcolor: 'grey.100',
                    width: SNO_WIDTH,
                    minWidth: SNO_WIDTH,
                    maxWidth: SNO_WIDTH,
                    position: 'sticky',
                    left: 0,
                    zIndex: 3,
                  }}
                >
                  #
                </TableCell>
                {columns.map((col) => (
                    <TableCell
                      key={col.key}
                      align={col.key === 'player_name' ? 'left' : 'center'}
                      sortDirection={orderBy === col.key ? order : false}
                      sx={{
                        fontWeight: 700,
                        whiteSpace: 'nowrap',
                        px: 1.25,
                        py: 1,
                        bgcolor: 'grey.100',
                        ...(col.key === 'player_name' && {
                          position: 'sticky',
                          left: SNO_WIDTH,
                          zIndex: 3,
                          borderRight: '2px solid',
                          borderRightColor: 'divider',
                        }),
                      }}
                    >
                      <Tooltip title={col.label === 'Total Value' ? (mode === 'batters' ? 'Batter run value + wicket run value × dismissal value. Not affected by the Batter / Shot toggle.' : 'Bowler run value + wicket run value × bowler dismissal value. Not affected by the Ball / Bowler toggle.') : ''} disableHoverListener={col.label !== 'Total Value'}>
                        <TableSortLabel
                          active={orderBy === col.key}
                          direction={orderBy === col.key ? order : 'asc'}
                          onClick={() => handleSort(col.key)}
                        >
                          {col.label}
                        </TableSortLabel>
                      </Tooltip>
                    </TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={columns.length + 1} align="center" sx={{ py: 6 }}>
                    <CircularProgress size={32} />
                  </TableCell>
                </TableRow>
              ) : pagedRows.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={columns.length + 1} align="center" sx={{ py: 6 }}>
                    <Typography variant="body2" color="text.secondary">
                      No projections match the current filters.
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : (
                pagedRows.map((row, idx) => (
                  <TableRow key={idx} hover sx={compare.includes(String(row['player_name'] ?? '')) ? { '& td:not(.cmp-heat)': { bgcolor: 'rgba(231, 76, 145, 0.07) !important' } } : undefined}>
                    <TableCell
                      align="center"
                      sx={{
                        whiteSpace: 'nowrap',
                        color: 'text.secondary',
                        width: SNO_WIDTH,
                        minWidth: SNO_WIDTH,
                        maxWidth: SNO_WIDTH,
                        position: 'sticky',
                        left: 0,
                        zIndex: 1,
                        bgcolor: 'background.paper',
                      }}
                    >
                      {page * rowsPerPage + idx + 1}
                    </TableCell>
                    {columns.map((col) => (
                        <TableCell
                          key={col.key}
                          className={heatOf(row, col.key) ? 'cmp-heat' : undefined}
                          align={col.key === 'player_name' ? 'left' : 'center'}
                          sx={{
                            whiteSpace: 'nowrap',
                            px: 1.25,
                            py: 0.75,
                            fontVariantNumeric: 'tabular-nums',
                            fontWeight: col.key === 'player_name' || heatOf(row, col.key)?.strong ? 700 : 400,
                            ...(heatOf(row, col.key) && { bgcolor: `${heatOf(row, col.key)!.bg} !important` }),
                            ...(col.key === 'player_name' && {
                              position: 'sticky',
                              left: SNO_WIDTH,
                              zIndex: 1,
                              bgcolor: 'background.paper',
                              borderRight: '2px solid',
                              borderRightColor: 'divider',
                            }),
                          }}
                        >
                          {col.key === 'player_name' ? (
                            <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5 }}>
                              <Tooltip title={compare.includes(String(row['player_name'])) ? 'Remove from comparison' : 'Add to comparison'}>
                                <span>
                                  <Checkbox
                                    size="small"
                                    checked={compare.includes(String(row['player_name']))}
                                    onChange={() => toggleCompare(String(row['player_name']))}
                                    sx={{ p: 0.25 }}
                                    inputProps={{ 'aria-label': `Compare ${String(row['player_name'])}` }}
                                  />
                                </span>
                              </Tooltip>
                              {formatValue(row[col.key] ?? null, col)}
                            </Box>
                          ) : formatValue(row[col.key] ?? null, col)}
                        </TableCell>
                    ))}
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>

        <TablePagination
          component="div"
          count={filteredSortedRows.length}
          page={page}
          onPageChange={(_e, newPage) => setPage(newPage)}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={(e) => {
            setRowsPerPage(parseInt(e.target.value, 10));
            setPage(0);
          }}
          rowsPerPageOptions={[10, 25, 50, 100]}
        />
      </Paper>
    </Box>
  );
};

export default Projections;
